import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import {
    getVehicleTypes,
    createVehicleType as createVehicleTypeConfig,
    updateVehicleType as updateVehicleTypeConfig,
    deleteVehicleType as deleteVehicleTypeConfig
} from '../../services/business/vehicleTypeService';

const VehicleTypeConfig = ({ user }) => {
    const { success, error } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [loading, setLoading] = useState(false);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        tenantId: user?.tenantId,
        name: '',
        code: '',
        volumeRange: { min: 0, max: 0 },
        weightRange: { min: 0, max: 0 }
    });

    useEffect(() => {
        if (user?.tenantId) {
            loadVehicleTypes();
        }
    }, [user?.tenantId]);

    const loadVehicleTypes = async () => {
        setLoading(true);
        try {
            const data = await getVehicleTypes(user.tenantId, false);
            setVehicleTypes(data);
        } catch (err) {
            error('Araç tipleri yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData(item);
        } else {
            setEditingItem(null);
            setFormData({
                tenantId: user.tenantId,
                name: '',
                code: '',
                volumeRange: { min: 0, max: 0 },
                weightRange: { min: 0, max: 0 }
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSave = async () => {
        try {
            if (!formData.name || !formData.code) {
                error('Lütfen tüm zorunlu alanları doldurun');
                return;
            }

            // Validation: Min cannot be greater than Max
            if (formData.volumeRange.min > formData.volumeRange.max) {
                error('Minimum hacim maksimum hacimden büyük olamaz.');
                return;
            }

            if (formData.weightRange.min > formData.weightRange.max) {
                error('Minimum ağırlık maksimum ağırlıktan büyük olamaz.');
                return;
            }

            if (editingItem) {
                await updateVehicleTypeConfig(editingItem.id, formData, user.tenantId);
                success('Araç tipi güncellendi');
            } else {
                await createVehicleTypeConfig(formData, user.tenantId);
                success('Araç tipi oluşturuldu');
            }

            handleCloseModal();
            loadVehicleTypes();
        } catch (err) {
            error('İşlem başarısız: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: 'Araç Tipini Sil',
            message: 'Bu araç tipini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteVehicleTypeConfig(id, user.tenantId);
                    success('Araç tipi silindi');
                    loadVehicleTypes();
                } catch (err) {
                    error('Silme başarısız: ' + err.message);
                }
            }
        });
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="badge badge-pending">Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h3 className="mb-xs">Araç Tipi Yapılandırmaları</h3>
                    <p className="text-secondary text-sm">
                        Sistemde kullanılacak araç tiplerini ve kapasitelerini tanımlayın
                    </p>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    + Yeni Araç Tipi
                </Button>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {vehicleTypes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🚛</div>
                        Henüz araç tipi tanımlanmamış.
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Araç Tipi</th>
                                <th>Kod</th>
                                <th>Hacim Aralığı (m³)</th>
                                <th>Ağırlık Aralığı (Ton)</th>
                                <th>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vehicleTypes.map(type => (
                                <tr key={type.id}>
                                    <td style={{ fontWeight: 'var(--font-bold)' }}>{type.name}</td>
                                    <td>
                                        <code style={{
                                            background: 'var(--bg-hover)',
                                            padding: '4px 8px',
                                            borderRadius: 'var(--radius-sm)',
                                            fontFamily: 'var(--font-mono)'
                                        }}>
                                            {type.code}
                                        </code>
                                    </td>
                                    <td>{type.volumeRange.min} - {type.volumeRange.max}</td>
                                    <td>{type.weightRange.min} - {type.weightRange.max}</td>
                                    <td>
                                        <span className={`badge ${type.isActive ? 'badge-completed' : 'badge-pending'}`}>
                                            {type.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-sm">
                                            <Button size="sm" variant="secondary" onClick={() => handleOpenModal(type)}>
                                                Düzenle
                                            </Button>
                                            <Button size="sm" variant="danger" onClick={() => handleDelete(type.id)}>
                                                Sil
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingItem ? 'Araç Tipi Düzenle' : 'Yeni Araç Tipi'}
            >
                <div className="form-group">
                    <label className="form-label">Araç Tipi Adı *</label>
                    <input
                        type="text"
                        className="input"
                        placeholder="Örn: Tır (13.60)"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">Kod *</label>
                    <input
                        type="text"
                        className="input"
                        placeholder="Örn: truck"
                        value={formData.code}
                        onChange={e => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
                        style={{ fontFamily: 'var(--font-mono)' }}
                    />
                    <small className="text-secondary">Küçük harf ve alt çizgi kullanın (örn: truck, lorry, van)</small>
                </div>

                <div className="grid grid-cols-2 gap-md">
                    <div className="form-group">
                        <label className="form-label">Min Hacim (m³)</label>
                        <input
                            type="number"
                            className="input"
                            min="0"
                            value={formData.volumeRange?.min || 0}
                            onChange={e => setFormData({
                                ...formData,
                                volumeRange: { ...formData.volumeRange, min: parseFloat(e.target.value) || 0 }
                            })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Max Hacim (m³)</label>
                        <input
                            type="number"
                            className="input"
                            min="0"
                            value={formData.volumeRange?.max || 0}
                            onChange={e => setFormData({
                                ...formData,
                                volumeRange: { ...formData.volumeRange, max: parseFloat(e.target.value) || 0 }
                            })}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-md">
                    <div className="form-group">
                        <label className="form-label">Min Ağırlık (Ton)</label>
                        <input
                            type="number"
                            className="input"
                            min="0"
                            value={formData.weightRange?.min || 0}
                            onChange={e => setFormData({
                                ...formData,
                                weightRange: { ...formData.weightRange, min: parseFloat(e.target.value) || 0 }
                            })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Max Ağırlık (Ton)</label>
                        <input
                            type="number"
                            className="input"
                            min="0"
                            value={formData.weightRange?.max || 0}
                            onChange={e => setFormData({
                                ...formData,
                                weightRange: { ...formData.weightRange, max: parseFloat(e.target.value) || 0 }
                            })}
                        />
                    </div>
                </div>

                <div className="flex gap-md justify-end mt-lg">
                    <Button variant="secondary" onClick={handleCloseModal}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default VehicleTypeConfig;
